import { ethers } from "ethers";

export function shortAddress(addr, chars = 4) {
  if (!addr) return "";
  return `${addr.slice(0, chars + 2)}...${addr.slice(-chars)}`;
}

export function formatUSDT(value, decimals = 2) {
  if (!value) return "0.00";
  try {
    const bn = ethers.BigNumber.isBigNumber(value)
      ? value
      : ethers.BigNumber.from(value.toString());
    const formatted = ethers.utils.formatUnits(bn, 18);
    const num = parseFloat(formatted);
    return num.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  } catch {
    return "0.00";
  }
}

export function formatUSDTCompact(value) {
  if (!value) return "0";
  try {
    const bn = ethers.BigNumber.isBigNumber(value)
      ? value
      : ethers.BigNumber.from(value.toString());
    const num = parseFloat(ethers.utils.formatUnits(bn, 18));
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(2) + "M";
    if (num >= 1_000) return (num / 1_000).toFixed(2) + "K";
    return num.toFixed(2);
  } catch {
    return "0";
  }
}

export function parseUSDT(value) {
  return ethers.utils.parseUnits(value.toString(), 18);
}

export function bytes32ToString(bytes32) {
  if (!bytes32) return "";
  try {
    return ethers.utils.parseBytes32String(bytes32);
  } catch {
    // Fallback: strip trailing nulls manually
    try {
      const bytes = ethers.utils.arrayify(bytes32);
      let end = bytes.length;
      while (end > 0 && bytes[end - 1] === 0) end--;
      return new TextDecoder().decode(bytes.slice(0, end));
    } catch {
      return "";
    }
  }
}

// Convert a JS string to a bytes32 the contract will accept.
// The contract's _validateName rejects any byte outside printable ASCII 32-126
// and any bytes32(0) input.
//
// Rules enforced HERE (so we can show a friendly message before the wallet fires):
//   - Trim whitespace
//   - Must be non-empty
//   - Every character must be printable ASCII (0x20-0x7E) — no accents, emoji,
//     tabs, or control chars. Multi-byte UTF-8 always contains bytes > 0x7F.
//   - After ASCII check, the byte-length equals the char-length, so we truncate
//     safely to 31 bytes (leaving room for the null terminator).
//
// Throws a plain Error with a user-readable message on invalid input.
export function stringToBytes32(str) {
  const s = String(str || "").trim();
  if (!s) {
    const e = new Error("Name cannot be empty.");
    e.code = "NAME_EMPTY";
    throw e;
  }
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 0x20 || c > 0x7E) {
      const e = new Error(
        `Name must use plain ASCII letters/numbers/symbols only. ` +
        `"${s.charAt(i)}" (position ${i + 1}) is not allowed — no accented letters, emoji, or special characters.`
      );
      e.code = "NAME_INVALID_CHAR";
      throw e;
    }
  }
  // Safe: every char is 1 byte, slicing chars = slicing bytes
  const truncated = s.slice(0, 31);
  return ethers.utils.formatBytes32String(truncated);
}

// Pure check — returns null if the name is valid, or an error string.
// Use for live form validation (no throw).
export function validateNameForContract(str) {
  try {
    stringToBytes32(str);
    return null;
  } catch (e) {
    return e.message;
  }
}

export function formatTimestamp(ts) {
  if (!ts) return "";
  const n = typeof ts === "number" ? ts : Number(ts.toString());
  if (!n) return "";
  const date = new Date(n * 1000);
  // Force UTC display so all users see the same time regardless of local zone
  return date.toLocaleString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  }) + " UTC";
}

export function timeAgo(ts) {
  if (!ts) return "";
  const n = typeof ts === "number" ? ts : Number(ts.toString());
  if (!n) return "";
  const seconds = Math.floor(Date.now() / 1000) - n;
  if (seconds < 60) return `${seconds}s ago`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}
